import { useState, useRef, useEffect, useCallback } from "react";
import ComponentsMapManagement from "./ComponentsMapManagement";
import { Close, Replay, ViewInAr } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Slide,
  Typography,
} from "@mui/material";
import ModelsManagement from "./ModelsManagement";
import { useSelector } from "react-redux";
import {
  modelsManagementService,
  myGeolocalisedServices,
  myThemesServices,
} from "../../services";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

const ComponentsManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isModelManagementOpen, setIsModelManagementOpen] = useState(false);
  // Refs for drag handling
  const panelRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const [animating, setAnimating] = useState(false);
  const [myComponents, setMyComponents] = useState(null);
  const [geolocalisedComponents, setGeolocalisedComponents] = useState(null);
  const [objectIdToDeleteFromMap, setObjectIdToDeleteFromMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backdropLock, setBackdropLock] = useState(false);

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );

  const fetchModels = useCallback(() => {
    setLoading(true);
    modelsManagementService
      .getAllConfigModels(isAdmin)
      .then((res) => {
        setMyComponents({
          ...myComponents,
          objects: res.data,
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [enqueueSnackbar, isAdmin, t, myComponents]);

  const fetchThemes = useCallback(() => {
    setLoading(true);
    myThemesServices
      .getThemes()
      .then((res) => {
        setMyComponents({
          ...myComponents,
          themes: res.data,
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [enqueueSnackbar, t, myComponents]);

  const fetchAll = useCallback(() => {
    Promise.all([
      modelsManagementService.getAllConfigModels(isAdmin),
      myGeolocalisedServices.getGeolocalisedComponents(),
      myThemesServices.getThemes(),
    ])
      .then((res) => {
        if (res.length > 0)
          setMyComponents({ objects: res[0].data, themes: res[2].data });
        else setMyComponents({ objects: [], themes: [] });
        if (res.length > 1) setGeolocalisedComponents(res[1]);
        else setGeolocalisedComponents([]);
        setBackdropLock(false);
      })
      .catch((err) => {
        setBackdropLock(true);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.unknownError"), {
            variant: "error",
          });
        setLoading(false);
      });
  }, [enqueueSnackbar, isAdmin, t]);

  useEffect(() => {
    if (!myComponents && !geolocalisedComponents) {
      fetchAll();
    }
  }, [myComponents, geolocalisedComponents, fetchAll]);

  const toggleModelManagement = () => {
    setIsModelManagementOpen(!isModelManagementOpen);

    // Set animating state to prevent dragging during animation
    setAnimating(true);
    setTimeout(() => {
      setAnimating(false);
    }, 500); // Slightly longer than animation to ensure completion
  };

  // TOUCH HANDLERS FOR MOBILE
  const handleTouchStart = (e) => {
    // Don't allow dragging during animation
    if (animating) return;

    touchStartRef.current = e.touches[0].clientY;
    touchYRef.current = 0;
    isDraggingRef.current = true;
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current || !isDraggingRef.current) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartRef.current;

    // Only allow dragging downward
    if (diff > 0) {
      touchYRef.current = diff;
      // Apply transform to the panel
      if (panelRef.current) {
        panelRef.current.style.transform = `translateY(${diff}px)`;
        panelRef.current.style.transition = "none";
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current) return;

    // Calculate threshold as 10% of viewport height
    const threshold = window.innerHeight * 0.1;

    if (touchYRef.current > threshold) {
      // Close the panel
      setIsModelManagementOpen(false);
    }

    // Reset position with animation
    if (panelRef.current) {
      panelRef.current.style.transform = "";
      panelRef.current.style.transition = "transform 0.3s ease";
    }

    // Reset tracking
    touchStartRef.current = null;
    touchYRef.current = 0;
    isDraggingRef.current = false;
  };

  // MOUSE HANDLERS FOR DESKTOP
  const handleMouseDown = (e) => {
    // Only respond to primary mouse button (left click)
    if (e.button !== 0) return;

    touchStartRef.current = e.clientY;
    touchYRef.current = 0;
    isDraggingRef.current = true;

    // Prevent text selection during drag
    // e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!touchStartRef.current || !isDraggingRef.current) return;

    const diff = e.clientY - touchStartRef.current;

    // Only allow dragging downward
    if (diff > 0) {
      touchYRef.current = diff;
      // Apply transform to the panel
      if (panelRef.current) {
        panelRef.current.style.transform = `translateY(${diff}px)`;
        panelRef.current.style.transition = "none";
      }
    }
  };

  const handleMouseUp = () => {
    if (!isDraggingRef.current) return;

    // Calculate threshold as 10% of viewport height
    const threshold = window.innerHeight * 0.1;

    if (touchYRef.current > threshold) {
      // Close the panel
      setIsModelManagementOpen(false);
    }

    // Reset position with animation
    if (panelRef.current) {
      panelRef.current.style.transform = "";
      panelRef.current.style.transition = "transform 0.3s ease";
    }

    // Reset tracking
    touchStartRef.current = null;
    touchYRef.current = 0;
    isDraggingRef.current = false;
  };

  // Add and remove global event listeners for mouse events
  useEffect(() => {
    if (isModelManagementOpen) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isModelManagementOpen]);

  return (
    <>
      <Backdrop
        open={backdropLock}
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
      >
        <Card>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "12px !important",
            }}
          >
            <Typography variant="body1">{t("toasts.unknownError")}</Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={fetchAll}
              startIcon={<Replay />}
              sx={{ marginTop: "1rem" }}
            >
              {t("conditionsDisplayer.retryButton")}
            </Button>
          </CardContent>
        </Card>
      </Backdrop>
      <ComponentsMapManagement
        myComponents={myComponents}
        geolocalisedComponents={geolocalisedComponents}
        objectIdToDeleteFromMap={objectIdToDeleteFromMap}
        setObjectIdToDeleteFromMap={setObjectIdToDeleteFromMap}
      />
      <IconButton
        onClick={toggleModelManagement}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          backgroundColor: "#d3Af5f",
          color: "#333",
          "&:hover": {
            backgroundColor: "#333",
            color: "#d3af5f",
          },
          zIndex: 10,
          width: 56,
          height: 56,
        }}
      >
        {isModelManagementOpen ? <Close /> : <ViewInAr />}
      </IconButton>

      {/* Enhanced sliding panel with cleaner animation */}
      <Slide
        direction="up"
        in={isModelManagementOpen}
        timeout={{
          enter: 450, // Make enter animation slightly longer
          exit: 350,
        }}
        easing={{
          enter: "cubic-bezier(0.0, 0.0, 0.2, 1)", // Material Design standard easing
          exit: "cubic-bezier(0.4, 0.0, 1, 1)",
        }}
      >
        <Box
          ref={panelRef}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: "90vh",
            backgroundColor: "#424242",
            color: "white",
            boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.3)",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 9,
            overflow: "auto",
            padding: "20px 0 0 0",
            // Only use transition for dragging, not for initial animation
            transition: "transform 0.3s ease",
            // Don't add any custom animations here
          }}
          onTouchStart={(e) => {
            if (!animating) handleTouchStart(e);
          }}
          onTouchMove={(e) => {
            if (!animating) handleTouchMove(e);
          }}
          onTouchEnd={(e) => {
            if (!animating) handleTouchEnd(e);
          }}
          onMouseDown={(e) => {
            if (!animating) handleMouseDown(e);
          }}
        >
          {/* Handle/indicator */}
          <Box
            sx={{
              width: "60px",
              height: "5px",
              backgroundColor: "#bdbdbd",
              borderRadius: "10px",
              margin: "0 auto 15px auto",
              cursor: "grab",
            }}
          />

          <ModelsManagement
            myComponents={myComponents}
            geolocalisedComponents={geolocalisedComponents}
            fetchModels={fetchModels}
            fetchThemes={fetchThemes}
            setObjectIdToDeleteFromMap={setObjectIdToDeleteFromMap}
            loading={loading}
            setLoading={setLoading}
          />
        </Box>
      </Slide>
    </>
  );
};

export default ComponentsManagement;
